import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Bell, BellOff, BellRing, Check, Loader2, Clock, Calendar, Sun, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { 
  getVapidKey, 
  subscribeToPush, 
  getNotificationPreferences, 
  updateNotificationPreferences 
} from "@/lib/api";

export default function NotificationSettings({ open, onOpenChange }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [preferences, setPreferences] = useState({
    visit_reminders_enabled: true,
    reminder_24h: true,
    reminder_1h: true,
    reminder_morning_of: true,
    email_reminders: false,
    sms_reminders: false
  });

  useEffect(() => {
    if (open) {
      checkPushSupport();
      loadPreferences();
    }
  }, [open]);

  const checkPushSupport = async () => {
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      setPushSupported(true);
      
      // Check if already subscribed
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setPushEnabled(!!subscription);
    }
  };

  const loadPreferences = async () => {
    setLoading(true);
    try {
      const prefs = await getNotificationPreferences();
      setPreferences(prefs);
    } catch (error) {
      console.error("Failed to load preferences:", error);
    } finally {
      setLoading(false);
    }
  };

  const enablePushNotifications = async () => {
    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error("Notification permission denied. Please enable in browser settings.");
        return;
      }

      // Get VAPID key
      const { publicKey } = await getVapidKey();
      
      // Convert base64 to Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };

      // Subscribe to push
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      // Send subscription to server
      await subscribeToPush({
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))))
        }
      });

      setPushEnabled(true);
      toast.success("Push notifications enabled!");
    } catch (error) {
      console.error("Failed to enable push:", error);
      toast.error("Failed to enable push notifications");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateNotificationPreferences(preferences);
      toast.success("Notification preferences saved!");
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to save preferences:", error);
      toast.error("Failed to save preferences");
    } finally {
      setSaving(false);
    }
  };

  const togglePreference = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary" />
            Notification Settings
          </DialogTitle>
          <DialogDescription>
            Configure how you want to receive reminders for your scheduled visits
          </DialogDescription>
        </DialogHeader>
        
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-6 py-4">
            {/* Push Notification Status */}
            <div className="p-4 rounded-lg border bg-muted/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {pushEnabled ? (
                    <BellRing className="w-5 h-5 text-green-500" />
                  ) : (
                    <BellOff className="w-5 h-5 text-muted-foreground" />
                  )}
                  <div>
                    <p className="font-medium">Push Notifications</p>
                    <p className="text-sm text-muted-foreground">
                      {pushEnabled ? "Enabled on this device" : "Not enabled"}
                    </p>
                  </div>
                </div>
                {pushSupported && !pushEnabled && (
                  <Button 
                    size="sm" 
                    onClick={enablePushNotifications}
                    data-testid="enable-push-btn"
                  >
                    Enable
                  </Button>
                )}
                {pushEnabled && (
                  <Badge className="bg-green-500">
                    <Check className="w-3 h-3 mr-1" />
                    Active
                  </Badge>
                )}
              </div>
              {!pushSupported && (
                <p className="text-xs text-amber-600 mt-2">
                  Push notifications are not supported on this browser
                </p>
              )}
            </div>

            {/* Visit Reminder Settings */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="visit-reminders" className="flex items-center gap-2 cursor-pointer">
                  <Calendar className="w-4 h-4 text-primary" />
                  <span className="font-medium">Visit Reminders</span>
                </Label>
                <Switch
                  id="visit-reminders"
                  checked={preferences.visit_reminders_enabled}
                  onCheckedChange={() => togglePreference('visit_reminders_enabled')}
                  data-testid="visit-reminders-toggle"
                />
              </div>

              {preferences.visit_reminders_enabled && (
                <div className="ml-6 space-y-3 border-l-2 border-primary/20 pl-4">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="reminder-24h" className="flex items-center gap-2 cursor-pointer text-sm">
                      <Clock className="w-4 h-4" />
                      24 hours before
                    </Label>
                    <Switch
                      id="reminder-24h"
                      checked={preferences.reminder_24h}
                      onCheckedChange={() => togglePreference('reminder_24h')}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label htmlFor="reminder-1h" className="flex items-center gap-2 cursor-pointer text-sm">
                      <Clock className="w-4 h-4" />
                      1 hour before
                    </Label>
                    <Switch
                      id="reminder-1h"
                      checked={preferences.reminder_1h}
                      onCheckedChange={() => togglePreference('reminder_1h')}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <Label htmlFor="reminder-morning" className="flex items-center gap-2 cursor-pointer text-sm">
                      <Sun className="w-4 h-4" />
                      Morning of visit (7 AM)
                    </Label>
                    <Switch
                      id="reminder-morning"
                      checked={preferences.reminder_morning_of}
                      onCheckedChange={() => togglePreference('reminder_morning_of')}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Additional Notification Channels */}
            <div className="space-y-4 pt-4 border-t">
              <p className="text-sm font-medium text-muted-foreground">Additional Channels</p>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="email-reminders" className="flex items-center gap-2 cursor-pointer text-sm">
                  <Smartphone className="w-4 h-4" />
                  Email reminders
                </Label>
                <Switch
                  id="email-reminders"
                  checked={preferences.email_reminders}
                  onCheckedChange={() => togglePreference('email_reminders')}
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="sms-reminders" className="flex items-center gap-2 cursor-pointer text-sm">
                  <Smartphone className="w-4 h-4" />
                  SMS reminders
                </Label>
                <Switch
                  id="sms-reminders"
                  checked={preferences.sms_reminders}
                  onCheckedChange={() => togglePreference('sms_reminders')}
                />
              </div>
            </div>
          </div>
        )}
        
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={saving || loading}
            data-testid="save-notification-prefs-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            Save Preferences
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
